<template>
    <div class="connect-wrapper">
        <g-loader v-if="fullscreenLoader" type="fixed" />
        <div v-if="context === 'profile'" class="margin-bottom-normal">
            <g-button @click="handleBack">Back</g-button>
        </div>

        <div v-if="failed || failurePage">
            <connect-network-failed :network="failed || failurePage" @go-back="handleTryAgain" />
        </div>
        <div v-else>
            <img class="logo-style" :src="NETWORK_CONNECTION_INFO_BY_ID[networkId].logo" alt="Logo" />
            <div class="h1">
                <g-typography tag="h1" font-weight="semibold" text-align="center">
                    <span>{{ NETWORK_CONNECTION_INFO_BY_ID[networkId].title }}</span>
                </g-typography>
            </div>
            <div class="text-wrapper">
                <div v-if="isInstagramById(networkId) && (isDiscoverable || discoverUnavailable)">
                    <g-alert
                        v-if="isDiscoverable"
                        message="Looks like you have the right type of account! Click connect to continue"
                        type="success"
                        show-icon
                    />
                    <g-alert
                        v-else
                        message="Just a few more steps! Click connect to continue"
                        type="success"
                        show-icon
                    />

                    <div class="margin-top-2">
                        <g-button
                            class="connect-button"
                            data-testid="connect"
                            size="xlarge"
                            shape="round"
                            theme="live"
                            type="primary"
                            @click="onConnect"
                        >
                            Connect
                        </g-button>
                    </div>

                    <div class="margin-top-2 margin-bottom-3">
                        <g-link
                            v-if="!fullConnectionRequired"
                            data-testid="dont-link-creator"
                            @click="handleDontLinkCreator"
                        >
                            <g-typography color="grey-6" size="h5" value="I don't want to connect my creator account" />
                        </g-link>
                    </div>
                </div>
                <div v-else>
                    <g-input
                        v-model="payload.username"
                        addon-before="@"
                        size="large"
                        :placeholder="NETWORK_CONNECTION_INFO_BY_ID[networkId].placeholder"
                    />

                    <g-typography class="g-mt-5" tag="div" text-align="left" color="grey-6">
                        {{ NETWORK_CONNECTION_INFO_BY_ID[networkId].url }}{{ payload.username }}
                    </g-typography>

                    <div class="margin-top-2">
                        <g-button
                            class="connect-button"
                            data-testid="continue"
                            size="xlarge"
                            shape="round"
                            theme="live"
                            type="primary"
                            :loading="loading"
                            :disabled="!usernameIsValid"
                            @click="handleContinue"
                        >
                            Continue
                        </g-button>
                    </div>

                    <div v-if="isInstagramById(networkId)" class="margin-top-2 margin-bottom-3">
                        <g-link v-if="fullConnectionRequired" @click="handleInstagramFaq">
                            <g-typography color="grey-6" size="h5" value="Why do I need a professional account?" />
                        </g-link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { debounce } from 'lodash';
    import { toRefs } from 'vue-demi';
    import { getMeta, popup } from '../../../../../src/helpers';
    import {
        INSTAGRAM_ID,
        INSTAGRAM_SLUG,
        INSTAGRAM_ACCOUNT_SLUG,
        NETWORK_INFO_BY_ID,
        NETWORK_CONNECTION_INFO_BY_ID,
        DISCOVERY_NO_BRAND_TOKEN,
        DISCOVERY_INVALID_BRAND_TOKEN,
        DISCOVERY_INVALID_BRAND_PERMISSIONS,
        CONNECTION_FAILURE_REASON_INCOMPLETE,
        CONNECTION_FAILURE_REASON_ACCOUNT_MISSING
    } from '../../../../constants/networks';
    import { NETWORK_CONNECTION_INFO_EVENTS_BY_ID } from '../../../../constants/analytics-events';
    import ConnectNetworkFailed from './ConnectNetworkFailed.vue';
    import WindowEventMixin from '../../../../mixins/WindowEventMixin';
    import useEventTracking from '../../../../../src/composables/use-event-tracking';
    import useNetworks from '../../../../../src/composables/use-networks';

    export default {
        name: 'NetworkConnectionInfo',
        mixins: [WindowEventMixin],
        components: {
            ConnectNetworkFailed
        },
        props: {
            context: {
                type: String,
                default: 'proposal'
            },
            networkId: {
                type: Number,
                required: true
            },
            contactId: {
                type: String,
                required: true
            },
            instagramGraphRequired: {
                type: Boolean,
                default: false
            },
            liveUrl: {
                type: String,
                default: ''
            },
            campaignId: {
                type: String,
                default: ''
            },
            proposalId: {
                type: String,
                default: ''
            },
            failurePage: {
                type: String,
                default: ''
            }
        },
        data() {
            return {
                loading: false,
                fullscreenLoader: false,
                discoverable: null,
                failed: null,
                payload: {
                    network_id: null,
                    username: undefined,
                    soft: true
                },
                messages: {
                    failure: 'network-error',
                    success: 'network-success'
                },
                debouncedformatUsername: debounce(() => {
                    if (this.isTiktokById(this.networkId) || this.isInstagramById(this.networkId)) {
                        this.payload.username = this.payload.username.replace(/[^0-9a-z._]/gi, '');
                    }
                }, 300),
                NETWORK_CONNECTION_INFO_BY_ID
            };
        },
        computed: {
            /**
             * The network authentication host
             *
             * @return {String}
             */
            host() {
                return `networkauth.${getMeta('routing-domain')}`;
            },

            /**
             * Whether the username is valid
             *
             * @return {Boolean}
             */
            usernameIsValid() {
                if (!this.payload.username) {
                    return false;
                }

                if (this.isInstagramById(this.networkId)) {
                    return this.payload.username.length >= 3;
                } else if (this.isTiktokById(this.networkId)) {
                    return this.payload.username.length >= 4;
                } else if (this.isPinterestById(this.networkId)) {
                    return this.payload.username.length >= 3 && this.payload.username.length <= 30;
                }

                return false;
            },

            /**
             * Whether the Instagram account is discoverable (creator/business)
             *
             * @return {Boolean}
             */
            isDiscoverable() {
                return this.discoverable && this.discoverable.success;
            },

            /**
             * Whether the grin account has configured a social account to allow for discovery of creators
             *
             * @return {Boolean}
             */
            discoverUnavailable() {
                return (
                    this.discoverable &&
                    !this.discoverable.success &&
                    (this.discoverable.message === DISCOVERY_NO_BRAND_TOKEN ||
                        this.discoverable.message === DISCOVERY_INVALID_BRAND_TOKEN ||
                        this.discoverable.message === DISCOVERY_INVALID_BRAND_PERMISSIONS)
                );
            },

            /**
             * Whether a full connection is required
             */
            fullConnectionRequired() {
                return (this.$route && this.$route.query && this.$route.query.stories) || this.instagramGraphRequired;
            }
        },
        methods: {
            /**
             * Instagram - Start the connection process
             */
            startInstagramConnection() {
                this.loading = true;
                this.startInstagramDiscovery();
            },

            /**
             * Instagram - Start the discovery process
             */
            startInstagramDiscovery() {
                const payload = {
                    username: this.payload.username.toLowerCase(),
                    contact_id: this.contactId
                };

                this.$http
                    .post('/api/v1/networks/instagram/discoverable', payload)
                    .then((res) => (this.discoverable = res.data))
                    .catch(() => this.handleInstagramDiscoverFailed())
                    .then(() => this.handlePostInstagramDiscover());
            },

            /**
             * Instagram - Continue connection process, after discovery
             */
            handlePostInstagramDiscover() {
                if (this.isDiscoverable || this.discoverUnavailable) {
                    this.loading = false;
                    return;
                }

                if (this.fullConnectionRequired) {
                    return this.handleFailure(INSTAGRAM_ACCOUNT_SLUG);
                }

                // When the brand is oauthed and discovery api returns "not discoverable"
                // the creator account cannot be found
                return this.handleFailure(INSTAGRAM_ACCOUNT_SLUG);
            },

            /**
             * Instagram - Handle the discovery failure
             */
            handleInstagramDiscoverFailed() {
                if (this.fullConnectionRequired) {
                    return this.handleFailure(INSTAGRAM_ACCOUNT_SLUG);
                }

                this.createSoftInstagramConnection();
            },

            /**
             * Starts the OAuth process by opening the popup
             */
            onConnect() {
                this.showPopup();
            },

            /**
             * Creates the OAuth URL for the popup
             */
            createOAuthUrl() {
                const safariWebview = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(navigator.userAgent);
                const androidWebview = /; wv/.test(navigator.userAgent);

                const slug = NETWORK_INFO_BY_ID[this.networkId].slug;

                let url = `https://${this.host}/contact/${this.$root.contact_id}/auth/${slug}/redirect?username=${this.payload.username}&start=true`;

                // Mobile opens a new tab instead of a popup
                // In this case we redirect in the new tab
                if (safariWebview || androidWebview) {
                    if (this.campaignId) {
                        url += `&origin=${window.location.origin}&live_url=${this.liveUrl}&campaign_id=${this.campaignId}&proposal_id=${this.proposalId}`;
                    } else if (this.context === 'brief') {
                        url += `&origin=${window.location.origin}&live_url=${this.liveUrl}&brief_id=${this.brief.id}`;
                    } else if (
                        this.$router?.history?.current.query.briefId &&
                        this.$router?.history?.current.query.deliverableId &&
                        this.$router?.history?.current.query.taskId &&
                        this.$route?.params?.live_url
                    ) {
                        url += `&origin=${window.location.origin}&live_url=${this.$route.params.live_url}&brief_id=${this.$route.query.briefId}&task_id=${this.$route.query.taskId}&deliverable_id=${this.$route.query.deliverableId}`;
                    } else if (this.$route) {
                        url += `&origin=${window.location.origin}&route=${this.$route.fullPath}`;
                    }
                }

                return url;
            },

            /**
             * Show the network oauth connection popup
             */
            showPopup() {
                this.loading = true;

                const pop = popup(this.createOAuthUrl(), 500, this.isPinterestById(this.networkId) ? 750 : 600);

                const interval = setInterval(() => {
                    if (pop.closed) {
                        this.loading = false;
                        clearInterval(interval);
                    }
                }, 200);

                this.listenForMessages();
            },

            /**
             * Listen for the failure message
             */
            listenForMessages() {
                this.listenForWindowMessage([this.messages.failure, this.messages.success], (event) => {
                    if (event.data.message === this.messages.failure) {
                        return this.handleFailure(event.data.network);
                    }

                    if (event.data.message === this.messages.success) {
                        this.$emit('popup-closed', this.networkId);
                        return this.$emit('go-back');
                    }
                });
            },

            /**
             * Send the given network error
             *
             * @param {string} network
             */
            async handleFailure(network) {
                if (network === INSTAGRAM_ACCOUNT_SLUG) {
                    this.payload.failure_reason = CONNECTION_FAILURE_REASON_ACCOUNT_MISSING;
                    await this.createFailedInstagramConnection();
                } else if (network === INSTAGRAM_SLUG) {
                    this.payload.failure_reason = CONNECTION_FAILURE_REASON_INCOMPLETE;
                    await this.createFailedInstagramConnection();
                }

                this.loading = false;
                if (this.context === 'profile') {
                    return (this.failed = network);
                }

                if (this.context === 'proposal') {
                    return this.$router.push({
                        name: 'v2.campaign.proposal.networks.failed',
                        params: { network }
                    });
                }

                if (this.context === 'brief') {
                    return this.$router.push({
                        name: 'v2.activation.brief.networks.failed',
                        params: { network }
                    });
                }
                this.$emit('go-back');
            },

            /**
             * Instagram - Connect softly
             */
            createSoftInstagramConnection() {
                this.loading = true;
                this.fullscreenLoader = true;

                this.payload.network_id = INSTAGRAM_ID;
                this.payload.url = this.payload.username;
                this.payload.failure_reason = CONNECTION_FAILURE_REASON_INCOMPLETE;

                this.$http
                    .post(`/api/v1/contacts/${this.contactId}/networks`, this.payload)
                    .then((res) => {
                        this.$emit('load-contact');
                        this.$root.reloadContact('networks');
                        return this.$emit('go-back');
                    })
                    .catch(() => {
                        this.handleFailure(INSTAGRAM_SLUG);
                    })
                    .finally(() => {
                        this.loading = false;
                        this.fullscreenLoader = false;
                    });
            },

            /**
             * Instagram - Creates a failed network connection
             */
            async createFailedInstagramConnection() {
                this.loading = true;

                try {
                    this.payload.network_id = INSTAGRAM_ID;
                    this.payload.url = this.payload.username;
                    await this.$http.post(`/api/v1/contacts/${this.contactId}/networks`, this.payload);

                    this.$emit('load-contact');
                    this.$root.reloadContact('networks');
                } catch (e) {
                    //
                }

                this.loading = false;
            },

            /**
             * Instagram - Show the FAQ
             */
            handleInstagramFaq() {
                this.$emit('show-instagram-faq');
            },

            /**
             * Clear the failed message
             */
            handleTryAgain() {
                this.failed = null;
                this.$emit('reset-failure-page');
            },

            handleBack() {
                this.goBackAnalyticsEvent();
                this.$emit('go-back');
            },

            handleDontLinkCreator() {
                this.dontLinkCreatorAnalyticsEvent();
                this.createSoftInstagramConnection();
            },

            handleContinue() {
                this.continueAnalyticsEvent();

                if (!this.isInstagramById(this.networkId)) {
                    this.onConnect();
                } else {
                    this.startInstagramConnection();
                }
            }
        },

        setup(props) {
            const { networkId } = toRefs(props);
            const { isInstagramById, isTiktokById, isPinterestById } = useNetworks();

            const goBackAnalyticsEvent = () => {
                useEventTracking().trackEvent(NETWORK_CONNECTION_INFO_EVENTS_BY_ID[networkId.value]?.back);
            };

            const continueAnalyticsEvent = () => {
                useEventTracking().trackEvent(NETWORK_CONNECTION_INFO_EVENTS_BY_ID[networkId.value]?.continue);
            };

            const dontLinkCreatorAnalyticsEvent = () => {
                useEventTracking().trackEvent(NETWORK_CONNECTION_INFO_EVENTS_BY_ID[networkId.value]?.dontLink, {
                    link_text: "I don't want to connect my creator account"
                });
            };

            return {
                isInstagramById,
                isTiktokById,
                isPinterestById,
                goBackAnalyticsEvent,
                continueAnalyticsEvent,
                dontLinkCreatorAnalyticsEvent
            };
        },

        /**
         * When the component is created
         */
        created() {
            if (this.$route && this.$route.query && this.$route.query.username) {
                this.payload.username = this.$route.query.username;
            }
        },

        watch: {
            payload: {
                deep: true,
                handler() {
                    this.debouncedformatUsername();
                }
            }
        }
    };
</script>

<style lang="scss" scoped>
    .connect-wrapper {
        text-align: center;

        .margin-bottom-normal {
            margin-bottom: $g-spacing-5;
        }

        .margin-top-2 {
            margin-top: $g-spacing-7;
        }
        .margin-bottom-3 {
            margin-bottom: $g-spacing-9;
        }
        .h1 {
            max-width: 320px;
            text-align: center;
            margin: 0 auto $g-spacing-6 auto;
        }
        .logo-style {
            width: 183px;
            margin-top: $g-spacing-7;
            margin-bottom: $g-spacing-7;
        }

        .text-wrapper {
            margin: 0 auto;
            width: 310px;
        }

        .connect-button {
            padding: 0 3rem;
        }
    }
</style>
